import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@material-ui/core/AppBar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Toolbar from '@material-ui/core/Toolbar';
import { makeStyles } from '@material-ui/core/styles';
import Head from 'next/head';
import classnames from 'classnames';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { useTranslation } from '../../src/i18n';
import Header from '../Header';
import LangSelect from '../LangSelect';
import Sidebar from '../Sidebar';
import Footer from '../Footer';
import SEOComponent from '../NextSeo';
import NextLink from '../NextLink';
import route from '../../constants/routes';
import ProfilesMenuDropdown from '../ProfilesMenuDropdown';
import { colors } from '../../constants/styleConstants';
import { selectIsLoggedInState } from '../../store/selectors';
import NotificationBanner from '../NotificationBanner';

const useStyles = makeStyles(theme => ({
  appBar: {
    zIndex: 999,
    backgroundColor: colors.white,
    color: '#6c6c6c',
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  toolbar: theme.mixins.toolbar,
  container: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    [theme.breakpoints.up('md')]: {
      alignItems: 'center',
    },
  },
  mobileLogin: {
    width: '100%',
    height: 26,
    backgroundColor: '#efefef',
    boxSizing: 'border-box',
    padding: '0 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: colors.text,
    '& > *': {
      fontSize: 10,
    },
    [theme.breakpoints.up(1025)]: {
      display: 'none',
    },
  },
  loginRegisterBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  loginRegisterLink: {
    fontSize: 12,
    opacity: 0.7,
    color: colors.text,
    textDecoration: 'none',
    '&:hover': {
      fontWeight: 'normal',
    },
  },
  children: {
    flexGrow: 1,
    width: '100%',
    maxWidth: 'calc(100% - 240px)',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
    },
  },
  mBannerWrapper: {
    marginTop: 83,
    flexGrow: 1,
    display: 'flex',
    maxWidth: '1280px',
    [theme.breakpoints.up('lg')]: {
      minWidth: '1280px',
    },
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      marginTop: 105,
    },
  },
  mContent: { display: 'flex' },
  layoutContent: {
    padding: '20px 30px',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      padding: 16,
    },
  },
  layoutContentReg: {
    padding: '0px 30px',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      padding: 16,
    },
  },
}));

export const LayoutContent = ({ children, variant, className }) => {
  const classes = useStyles();
  return (
    <div
      className={classnames(
        {
          [classes.layoutContent]: variant === 'top',
          [classes.layoutContentReg]: variant !== 'top',
        },
        className,
      )}
    >
      {children}
    </div>
  );
};

LayoutContent.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
  variant: PropTypes.oneOf(['top', 'regular']),
  className: PropTypes.string,
};

LayoutContent.defaultProps = {
  variant: 'top',
};

const Layout = ({ children, title, isLoggedIn, customBreadcrumbs, seo }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [mobileOpen, setMobileOpen] = useState(false);
  function handleDrawerToggle() {
    setMobileOpen(!mobileOpen);
  }

  return (
    <div className={classes.root}>
      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/images/favicon/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/images/favicon/favicon-16x16.png" />
        <link rel="manifest" href="/images/favicon/site.webmanifest" />
      </Head>
      <SEOComponent title={`${title} - Operabase`} {...seo} />
      <CssBaseline />
      <AppBar position="fixed" className={classes.appBar}>
        <div className={classes.mobileLogin}>
          <LangSelect />
          <div className={classes.loginRegisterBlock}>
            {/* <VpnKeyIcon className={classes.root}/> */}
            {isLoggedIn ? (
              <ProfilesMenuDropdown />
            ) : (
              <span>
                <NextLink href={route.LOGIN} className={classes.loginRegisterLink}>
                  {t('operabase.general.m_LOGIN')}
                </NextLink>
                {' / '}
                <NextLink href={route.REGISTER} className={classes.loginRegisterLink}>
                  {t('operabase.general.m_REGISTRATION')}
                </NextLink>
              </span>
            )}
          </div>
        </div>
        <Toolbar disableGutters>
          <Header
            handleDrawerToggle={handleDrawerToggle}
            title={title}
            isLoggedIn={isLoggedIn}
            customBreadcrumbs={customBreadcrumbs}
          />
        </Toolbar>
      </AppBar>

      <div className={classes.mBannerWrapper}>
        <NotificationBanner />
        <div className={classes.mContent}>
          <Sidebar mobileOpen={mobileOpen} closeMobileDrawer={handleDrawerToggle} />
          <div className={classes.children}>{children}</div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

Layout.propTypes = {
  children: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
  title: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  customBreadcrumbs: PropTypes.array,
  seo: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  isLoggedIn: selectIsLoggedInState(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(Layout);
